/**
 * Schemas Zod pros payloads dos tipos de proposta além de contrato-recorrente:
 *  - Projeto (infra, CFTV, controle-acesso, firewall, migrações)
 *  - Venda de material (peças, computador, distribuidor)
 *  - Venda de projeto/serviço (migrações, instalações, consultorias)
 *
 * MVP: valida estrutura mínima. Pricing detalhado (margem, custo) é calculado
 * server-side em iteração futura quando integrar com tabela de SKUs do Omie.
 */

import { z } from 'zod';

import { textoLongoSchema } from './common.js';

// ── Item base ─────────────────────────────────────────────────────────

export const itemMaterialSchema = z.object({
  produtoId: z.string().optional(),
  codigo: z.string().max(50).optional(),
  descricao: z.string().min(1).max(500),
  unidade: z.string().max(20).optional(),
  quantidade: z.number().positive(),
  valorUnitario: z.number().nonnegative(),
  custoUnitario: z.number().nonnegative().optional(),
  valorTotal: z.number().nonnegative(),
});

export const itemServicoSchema = z.object({
  servicoId: z.string().optional(),
  codigo: z.string().max(50).optional(),
  descricao: z.string().min(1).max(500),
  horasEstimadas: z.number().nonnegative().optional(),
  valorUnitario: z.number().nonnegative(),
  custoUnitario: z.number().nonnegative().optional(),
  valorTotal: z.number().nonnegative(),
});

export const etapaCronogramaSchema = z.object({
  nome: z.string().min(1).max(200),
  duracaoDias: z.number().int().positive(),
  dependencias: z.array(z.string()).optional(),
});

// ── Projeto (infra / CFTV / controle-acesso) ──────────────────────────

export const projetoPayloadSchema = z.object({
  descricao: z.string().min(1).max(2000),
  escopo: z.string().min(1).max(5000),
  materiais: z.array(itemMaterialSchema).max(200),
  servicos: z.array(itemServicoSchema).max(50),
  prazoExecucao: z.string().min(1).max(100),
  garantia: z.string().min(1).max(200),
  cronograma: z.array(etapaCronogramaSchema).max(50).optional(),
  observacoes: textoLongoSchema.optional(),
});

// ── Venda de material ─────────────────────────────────────────────────

export const vendaMaterialPayloadSchema = z.object({
  itens: z.array(itemMaterialSchema).min(1).max(200),
  formaPagamento: z.enum(['a-vista', 'parcelado', 'cartao', 'boleto']).optional(),
  parcelas: z.number().int().min(1).max(60).optional(),
  freteIncluso: z.boolean().optional(),
  prazoEntrega: z.string().max(100).optional(),
  observacoes: textoLongoSchema.optional(),
});

// ── Venda de projeto/serviço ──────────────────────────────────────────

export const modeloServicoSchema = z.enum([
  'migracao-m365',
  'migracao-servidor',
  'organizacao-rack',
  'implantacao-firewall',
  'consultoria-seguranca',
  'recuperacao-desastre',
  'personalizado',
]);

export const vendaServicoPayloadSchema = z.object({
  modelo: modeloServicoSchema,
  descricao: z.string().min(1).max(2000),
  escopo: z.string().min(1).max(5000),
  servicos: z.array(itemServicoSchema).min(1).max(50),
  cronograma: z.array(etapaCronogramaSchema).max(50).optional(),
  garantia: z.string().max(200).optional(),
  observacoes: textoLongoSchema.optional(),
});
